import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import { fetch_all_missions, upload_missions_csv } from '../../api/missionsAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import { MdAdd, MdEdit, MdUpload, MdDownload, MdClose, MdCheckCircle } from 'react-icons/md';
import { tidy_date, tidy_time_difference } from '../../utils/helper';
import DataTable from '../../components/ui/DataTable'
import Button from '../../components/ui/Button'
import FileUploadField from '../../components/FileUploadField'
import notify from '../../utils/IANotification'

export default function MissionsPage() {
  const navigate = useNavigate()
  const { isAuthorised, userRole } = useFetchAuthorisation("user")
  let user_data = useFetchUserData()

  const [missionsData, setMissionsData] = useState([])
  const [loading, setLoading] = useState(true)

  // CSV Import State
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)

  useEffect(() => {
    fetchMissions()
  }, [])

  const fetchMissions = async () => {
    setLoading(true)
    const data = await fetch_all_missions()
    if (data?.status === 200) {
      setMissionsData(data.data || [])
    }
    setLoading(false)
  }

  const downloadSampleCsv = () => {
    const content = "Title,Country Code,Description,Start Date,End Date\nCambodia Outreach Trip 2026,KH,Annual youth & medical mission trip to Siem Reap,2026-11-10 09:00,2026-11-17 18:00\nNorthern Thailand Mission,TH,Tribal church planting & community development,2026-12-01 08:00,2026-12-08 17:00";
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'sample_missions.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleFileSelected = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        setCsvText(e.target.result || '')
      }
      reader.readAsText(file)
    }
  }

  const handleCsvUpload = async () => {
    if (!csvText.trim()) {
      notify({ type: "error", message: "Please select or paste CSV content first" })
      return
    }

    setCsvUploading(true)
    setCsvResult(null)

    const res = await upload_missions_csv(csvText)
    setCsvUploading(false)

    if (res?.status === 200) {
      setCsvResult({ success: true, message: res.message || "Missions imported successfully!" })
      notify({ type: "success", message: res.message || "Missions imported successfully!" })
      fetchMissions()
      setTimeout(() => {
        setShowCsvModal(false)
        setCsvText('')
        setCsvResult(null)
      }, 1500)
    } else {
      setCsvResult({ success: false, message: res?.message || "Failed to import missions CSV" })
      notify({ type: "error", message: res?.message || "Failed to import missions CSV" })
    }
  }

  const table_columns = [
    {
      header: "Title",
      accessor: "title"
    },
    {
      header: "Date & Time",
      accessor: "start_datetime",
      modifier: (data, row) => `${tidy_date(row?.start_datetime)} - ${tidy_date(row?.end_datetime)}`
    },
    {
      header: "Last Updated",
      accessor: "updatedAt",
      modifier: (data, row) => `${tidy_date(data)} (${tidy_time_difference(data)})`
    }
  ]

  return (
    <AdminLayout
      navSelected={"Missions"}
      toolbar={
        <>
          <h1 className='text-lg font-semibold text-gray-900'>Missions</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={MdUpload}
              onClick={() => {
                setCsvText('')
                setCsvResult(null)
                setShowCsvModal(true)
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                navigate("/admin/missions/details?mission_id=new")
              }}
            >
              New Mission
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <h1 className="text-gray-500 font-medium">Loading missions...</h1>
        </div>
      ) : (
        <div className="w-full">
          <DataTable
            columns={table_columns}
            data={missionsData}
            enableActions
            actions={(row) => {
              return (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={MdEdit}
                    onClick={() => {
                      navigate("/admin/missions/details?mission_id=" + row.id)
                    }}
                  >
                    View
                  </Button>
                </div>
              )
            }}
          />
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4">
            <button
              onClick={() => setShowCsvModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <MdClose size={22} />
            </button>

            <div className="flex items-center justify-between pr-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Missions via CSV</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Upload a CSV file or paste content below. Expected headers: <code className="bg-gray-100 px-1 rounded text-gray-800 font-mono">Title, Country Code, Description, Start Date, End Date</code>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={MdDownload}
                onClick={downloadSampleCsv}
              >
                Download Sample CSV
              </Button>
            </div>

            {/* File Upload Component */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <FileUploadField
                item_index="missions_csv"
                max_file_size={5}
                max_files={1}
                text="Select or Drag Missions CSV File"
                subtext="Supported format: .csv"
                onChange={(files) => handleFileSelected(files)}
              />
            </div>

            {/* Text Preview / Edit Area */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CSV Content Preview / Manual Edit:
              </label>
              <textarea
                rows={6}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`Title,Country Code,Description,Start Date,End Date\nCambodia Outreach Trip 2026,KH,Annual youth & medical mission trip,2026-11-10 09:00,2026-11-17 18:00`}
                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {csvResult && (
              <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                <MdCheckCircle size={18} />
                {csvResult.message}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                icon={MdUpload}
                onClick={handleCsvUpload}
                disabled={csvUploading || !csvText.trim()}
              >
                {csvUploading ? 'Uploading...' : 'Process & Upload Missions'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
